import React, { useState } from "react";
import { ArrowUp, ArrowDown, Pause } from "lucide-react";

const SIZE_OPTIONS = [1, 2, 5, 10];

/**
 * Order ticket — LONG / SHORT / WAIT.
 * Emits onExecute({ side, sizePct, slPct, tpPct }).
 */
export const OrderPanel = ({ disabled, onExecute, onWait, currentPrice }) => {
  const [side, setSide] = useState(null);
  const [sizePct, setSizePct] = useState(2);
  const [slPct, setSlPct] = useState(2);
  const [tpPct, setTpPct] = useState(4);

  const submit = () => {
    if (!side) return;
    onExecute({ side, sizePct, slPct, tpPct });
    setSide(null);
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#0a0b14] p-4"
      data-testid="sim-order-panel"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70">
          Panel de órdenes
        </span>
        <span className="font-mono text-[10px] text-white/40">
          Precio: ${currentPrice ? currentPrice.toFixed(currentPrice < 100 ? 2 : 0) : "—"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => setSide("long")}
          data-testid="sim-btn-long"
          disabled={disabled}
          className={`flex items-center justify-center gap-1 rounded-lg border py-2 text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-40 ${
            side === "long"
              ? "border-emerald-400 bg-emerald-400/25 text-emerald-200 shadow-[0_0_18px_rgba(0,230,118,0.35)]"
              : "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-300 hover:bg-emerald-400/15"
          }`}
        >
          <ArrowUp className="h-3.5 w-3.5" /> Long
        </button>
        <button
          type="button"
          onClick={() => setSide("short")}
          data-testid="sim-btn-short"
          disabled={disabled}
          className={`flex items-center justify-center gap-1 rounded-lg border py-2 text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-40 ${
            side === "short"
              ? "border-rose-400 bg-rose-400/25 text-rose-200 shadow-[0_0_18px_rgba(255,51,85,0.35)]"
              : "border-rose-400/30 bg-rose-400/[0.08] text-rose-300 hover:bg-rose-400/15"
          }`}
        >
          <ArrowDown className="h-3.5 w-3.5" /> Short
        </button>
        <button
          type="button"
          onClick={() => { setSide(null); onWait?.(); }}
          data-testid="sim-btn-wait"
          disabled={disabled}
          className="flex items-center justify-center gap-1 rounded-lg border border-white/20 bg-white/[0.04] py-2 text-xs font-bold uppercase tracking-wider text-white/65 transition-all hover:bg-white/10 disabled:opacity-40"
        >
          <Pause className="h-3.5 w-3.5" /> Wait
        </button>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/50">
            Tamaño (% capital)
          </p>
          <div className="mt-1.5 grid grid-cols-4 gap-1.5">
            {SIZE_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSizePct(s)}
                data-testid={`sim-size-${s}`}
                disabled={disabled}
                className={`rounded-md border py-1.5 text-[11px] font-bold transition-all disabled:opacity-40 ${
                  sizePct === s
                    ? "border-cyan-400 bg-cyan-400/20 text-cyan-200"
                    : "border-white/15 bg-white/[0.03] text-white/55 hover:bg-white/10"
                }`}
              >
                {s}%
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/50">
              Stop loss %
            </span>
            <input
              type="number"
              value={slPct}
              min={0.1}
              step={0.1}
              max={50}
              onChange={(e) => setSlPct(Math.max(0.1, parseFloat(e.target.value) || 0))}
              disabled={disabled}
              data-testid="sim-sl-input"
              className="mt-1 w-full rounded-md border border-white/15 bg-black/40 px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-rose-400/60"
            />
          </label>
          <label className="block">
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/50">
              Take profit %
            </span>
            <input
              type="number"
              value={tpPct}
              min={0.1}
              step={0.1}
              max={200}
              onChange={(e) => setTpPct(Math.max(0.1, parseFloat(e.target.value) || 0))}
              disabled={disabled}
              data-testid="sim-tp-input"
              className="mt-1 w-full rounded-md border border-white/15 bg-black/40 px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-emerald-400/60"
            />
          </label>
        </div>

        <button
          type="button"
          disabled={disabled || !side}
          onClick={submit}
          data-testid="sim-btn-execute"
          className={`relative mt-2 w-full overflow-hidden rounded-lg py-2.5 text-xs font-bold uppercase tracking-[0.22em] transition-all ${
            side === "long"
              ? "bg-emerald-400 text-black hover:shadow-[0_0_30px_rgba(0,230,118,0.4)]"
              : side === "short"
              ? "bg-rose-400 text-black hover:shadow-[0_0_30px_rgba(255,51,85,0.4)]"
              : "bg-white/10 text-white/40"
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          Ejecutar
        </button>
      </div>
    </div>
  );
};

export default OrderPanel;
